import { Form, Input, Typography } from "antd";
import { isChallengeResponse, LoginResponse, useContinueAuthenticationMutation, useLoginMutation } from "auth/api/authApi";
import { setAuthToken } from "auth/authToken";
import { PrimaryButton } from "components/Button/ActionButton";
import { Palette } from "config/palette";
import { useBackUrl } from "hooks/useBackUrl";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setError } from "redux/errorSlice";
import { StoreDispatch } from "redux/store";

const { Text } = Typography;

interface ChallengeState {
    username: string;
    challengeState: string;
    prompt: string;
}

const buttonStyle = {
    backgroundColor: Palette.HeroGreen,
    borderColor: Palette.HeroGreen,
    color: "#fff",
    fontWeight: "bold",
} as const;

export const BasicLoginPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<StoreDispatch>();
    const backUrl = useBackUrl();
    const [login, { isLoading: isLoggingIn }] = useLoginMutation();
    const [continueAuthentication, { isLoading: isContinuing }] = useContinueAuthenticationMutation();
    const [challenge, setChallenge] = useState<ChallengeState | null>(null);

    const handleLoginResult = (result: LoginResponse, username: string) => {
        if (isChallengeResponse(result)) {
            setChallenge({ username, challengeState: result.challengeState, prompt: result.prompt });
        } else {
            setAuthToken(result.token);
            navigate(backUrl());
        }
    };

    const handleCredentials = async ({ username, password }: { username: string; password: string }) => {
        try {
            const result = await login({ username, password }).unwrap();
            handleLoginResult(result, username);
        } catch {
            dispatch(setError("Login failed. Please check your credentials."));
        }
    };

    const handleChallengeResponse = async ({ challengeResponse }: { challengeResponse: string }) => {
        if (!challenge) return;
        try {
            const result = await continueAuthentication({
                username: challenge.username,
                challengeState: challenge.challengeState,
                challengeResponse,
            }).unwrap();
            handleLoginResult(result, challenge.username);
        } catch {
            dispatch(setError("Login failed. Please check your response."));
        }
    };

    if (challenge) {
        return (
            <>
                <Text style={{ display: "block", marginBottom: 16 }}>
                    The server says: <strong>{challenge.prompt}</strong>
                </Text>
                <Form name="challenge" layout="vertical" autoComplete="off" onFinish={handleChallengeResponse}>
                    <Form.Item
                        label="Response"
                        name="challengeResponse"
                        rules={[{ required: true, message: "Please enter your response!" }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item label={null}>
                        <PrimaryButton type="primary" htmlType="submit" size="large" style={buttonStyle}>
                            {isContinuing ? "Verifying..." : "Submit"}
                        </PrimaryButton>
                    </Form.Item>
                </Form>
            </>
        );
    }

    return (
        <Form name="basic" layout="vertical" initialValues={{ remember: true }} autoComplete="off" onFinish={handleCredentials}>
            <Form.Item label="Username" name="username" rules={[{ required: true, message: "Please input your username!" }]}>
                <Input />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true, message: "Please input your password!" }]}>
                <Input.Password />
            </Form.Item>
            <Form.Item label={null}>
                <PrimaryButton type="primary" htmlType="submit" size="large" style={buttonStyle}>
                    {isLoggingIn ? "Logging in..." : "Login"}
                </PrimaryButton>
            </Form.Item>
        </Form>
    );
};
